import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { Document } from "../types/database";

interface DocumentFormProps {
  document?: Document; // If provided, we're editing an existing document
  inventoryItemId?: string;
  assetId?: string;
  jobId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function DocumentForm({
  document,
  inventoryItemId,
  assetId,
  jobId,
  onSuccess,
  onCancel,
}: DocumentFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const isEditing = !!document;

  const [formData, setFormData] = useState({
    name: document?.name || "",
    type: document?.type || "manual",
    url: document?.url || "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const uploadDocument = async (
    _file: File | null,
    _userId: string,
    _organizationId: string
  ): Promise<string> => {
    if (!file) return formData.url; // If no new file, keep existing URL

    // TODO: Add document upload logic later
    console.warn("Document upload not implemented yet");
    return "";
  };

  const documentMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");

      try {
        // Upload document if there's a file
        if (!currentOrganization?.id)
          throw new Error("No organization selected");
        const documentUrl = await uploadDocument(
          file,
          user.id,
          currentOrganization.id
        );

        if (isEditing && document) {
          // Update existing document
          await uploadDocument(file, user.id, currentOrganization.id); // for signature match, even if unused
          return await inventoryService.updateDocument(
            document.id,
            {
              name: formData.name,
              type: formData.type as
                | "manual"
                | "datasheet"
                | "warranty"
                | "other",
              url: documentUrl,
            },
            currentOrganization.id
          );
        } else {
          // Create new document
          if (!currentOrganization) throw new Error("No organization selected");
          return await inventoryService.addDocument({
            name: formData.name,
            type: formData.type as
              | "manual"
              | "datasheet"
              | "warranty"
              | "other",
            url: documentUrl,
            inventory_item_id: inventoryItemId || null,
            asset_id: assetId || null,
            job_id: jobId || null,
            organization_id: currentOrganization.id,
            created_by: user.id,
          });
        }
      } catch (error: any) {
        setError(error.message || "Failed to save document");
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries
      if (inventoryItemId) {
        queryClient.invalidateQueries({
          queryKey: ["inventory", inventoryItemId],
        });
      }
      if (assetId) {
        queryClient.invalidateQueries({ queryKey: ["asset", assetId] });
      }
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      }

      if (onSuccess) onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.name) {
      setError("Document name is required");
      return;
    }

    if (!formData.url && !file) {
      setError("Please select a file or provide a URL");
      return;
    }

    documentMutation.mutate();
  };

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">
        {isEditing ? "Edit Document" : "Add Document"}
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Document Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            required
          />
        </div>

        {/* Document Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document Type
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="manual">Manual</option>
            <option value="datasheet">Datasheet</option>
            <option value="warranty">Warranty</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Document File */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isEditing ? "Replace File" : "Upload File"}
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {isEditing && (
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to keep the current file
            </p>
          )}
        </div>

        {/* Document URL (for external documents) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Or External URL
          </label>
          <input
            type="url"
            name="url"
            value={formData.url}
            onChange={handleChange}
            placeholder="https://example.com/document.pdf"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            You can provide a URL instead of uploading a file
          </p>
        </div>

        {/* Current Document (if editing) */}
        {isEditing && formData.url && (
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm font-medium text-gray-700">
              Current Document:
            </p>
            <a
              href={formData.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              View Document
            </a>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={documentMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            disabled={documentMutation.isPending}
          >
            {documentMutation.isPending ? "Saving..." : "Save Document"}
          </button>
        </div>
      </form>
    </div>
  );
}
